import React, { useEffect, useState } from "react";
import { useTheme } from "../hooks/useTheme";
import styles from "./ColorSwatch.module.scss";

interface ColorSwatchProps {
  name: string;
  colorVar: string;
}

export function ColorSwatch({ name, colorVar }: ColorSwatchProps) {
  const { theme } = useTheme();
  const [color, setColor] = useState("");
  const [rgbValue, setRgbValue] = useState("");

  useEffect(() => {
    function resolveColor(varName: string): { hex: string; rgb: string } {
      const computedStyle = getComputedStyle(document.documentElement);
      const value = computedStyle.getPropertyValue(varName.trim()).trim();

      if (value.startsWith("var(")) {
        const nestedVar = value.match(/var\((--[^,)]+)/)?.[1];
        if (nestedVar) {
          return resolveColor(nestedVar);
        }
      }

      if (value.startsWith("rgb")) {
        const numbers = value.match(/\d+/g);
        if (numbers && numbers.length >= 3) {
          const [r, g, b] = numbers.map(Number);
          return {
            hex: rgbToHex(r, g, b),
            rgb: value,
          };
        }
      }

      if (value.startsWith("#")) {
        return {
          hex: value.toUpperCase(),
          rgb: hexToRgb(value),
        };
      }

      return {
        hex: value.toUpperCase(),
        rgb: value,
      };
    }

    function rgbToHex(r: number, g: number, b: number): string {
      const toHex = (n: number): string => {
        const hex = n.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }

    function hexToRgb(hex: string): string {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (result) {
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgb(${r}, ${g}, ${b})`;
      }
      return hex;
    }

    const cleanVarName = colorVar.startsWith("--")
      ? colorVar
      : colorVar.replace(/var\((--[^,)]+).*\)/, "$1");

    // Function to update colors
    function updateColors() {
      const resolvedColor = resolveColor(cleanVarName);
      setColor(resolvedColor.hex);
      setRgbValue(resolvedColor.rgb);
    }

    // Create a MutationObserver to watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "data-theme") {
          updateColors();
        }
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    // Initial color update
    updateColors();

    // Cleanup
    return () => observer.disconnect();
  }, [colorVar, theme]);

  const isDark = (hex: string): boolean => {
    if (!hex) return false;

    let color = hex.replace("#", "");

    if (color.length === 3) {
      color = color
        .split("")
        .map((c) => c + c)
        .join("");
    }

    if (color.length !== 6) return false;

    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
  };

  return (
    <div className={styles.container}>
      <div className={styles.details}>
        <div className={styles.name}>{name}</div>
        <div className={styles.variable}>{colorVar}</div>
      </div>
      <div
        className={styles.swatch}
        style={{ backgroundColor: `var(${colorVar})` }}
      >
        <div className={styles.swatchContent}>
          <div className={styles.colorInfo}>
            <span
              className={styles.colorValue}
              style={{ color: isDark(color) ? "#FFFFFF" : "#000000" }}
            >
              <div className={styles.colorName}>{color || "N/A"}</div>
              {rgbValue || "N/A"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
